import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Trash2 } from "lucide-react";
import { TimeSlotSelect } from "./TimeSlotSelect";
import { DatePicker } from "./DatePicker";
import { EMPTY_BLOCKED } from "./siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-3 py-2 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

const labelCls =
  "mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60";

export function OrderEditModal({ order, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: order.name,
    phone: order.phone,
    date: order.date || "",
    time: order.time || "",
    notes: order.notes || "",
    status: order.status,
    items: order.items.map((it) => ({ ...it })),
  });
  const [saving, setSaving] = useState(false);

  const setItem = (i, key, val) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((x, j) => (j === i ? { ...x, [key]: val } : x)),
    }));
  const addItem = () =>
    setForm((f) => ({
      ...f,
      items: [...f.items, { name: "", price: 0, quantity: 1 }],
    }));
  const removeItem = (i) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  const total = form.items.reduce((s, it) => {
    const price = Number(it.price) || 0;
    const qty = Number(it.quantity) || 0;
    // Promo : 3 Poulet grillé = 30,50 € (uniquement exactement 3)
    if (it.name === "Poulet grillé" && qty === 3) return s + 30.5;
    return s + price * qty;
  }, 0);

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Nom et téléphone obligatoires.");
      return;
    }
    const items = form.items
      .filter((it) => it.name.trim() && Number(it.quantity) >= 1)
      .map((it) => ({
        name: it.name.trim(),
        price: Number(it.price) || 0,
        quantity: Number(it.quantity),
      }));
    if (!items.length) {
      toast.error("Choisissez au moins un plat.");
      return;
    }
    if (!form.date) {
      toast.error("Date de retrait obligatoire.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/orders/${order.id}`,
        {
          name: form.name.trim(),
          phone: form.phone.trim(),
          date: form.date,
          time: form.time,
          notes: form.notes,
          status: form.status,
          items,
        },
        { withCredentials: true }
      );
      toast.success("Commande mise à jour !");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-order-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-5"
    >
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-sm border border-[#102C1F]/20 bg-[#F4F1EB] p-8">
        <button
          data-testid="edit-order-close"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute right-4 top-4 text-[#102C1F]/50 transition-colors duration-300 hover:text-[#102C1F]"
        >
          <X size={20} />
        </button>
        <h3 className="mb-6 font-display text-2xl italic tracking-tight">
          Modifier la commande
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelCls}>Nom *</span>
              <input
                data-testid="edit-order-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={field}
              />
            </label>
            <label className="block">
              <span className={labelCls}>Téléphone *</span>
              <input
                data-testid="edit-order-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={field}
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelCls}>Date de retrait *</span>
              <DatePicker
                testid="edit-order-date"
                value={form.date}
                onChange={(v) => setForm({ ...form, date: v })}
                blocked={EMPTY_BLOCKED}
                allowPast
              />
            </label>
            <label className="block">
              <span className={labelCls}>Heure</span>
              <TimeSlotSelect
                scope="orders"
                date={form.date}
                value={form.time}
                onChange={(v) => setForm({ ...form, time: v })}
                testid="edit-order-time"
                className={field}
              />
            </label>
          </div>

          <div>
            <span className={labelCls}>Articles *</span>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    data-testid={`edit-order-item-name-${i}`}
                    placeholder="Nom du plat"
                    value={it.name}
                    onChange={(e) => setItem(i, "name", e.target.value)}
                    className={field}
                  />
                  <input
                    data-testid={`edit-order-item-price-${i}`}
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="€"
                    title="Prix unitaire"
                    value={it.price}
                    onChange={(e) => setItem(i, "price", e.target.value)}
                    className={`${field} w-24`}
                  />
                  <input
                    data-testid={`edit-order-item-qty-${i}`}
                    type="number"
                    min="1"
                    title="Quantité"
                    value={it.quantity}
                    onChange={(e) => setItem(i, "quantity", e.target.value)}
                    className={`${field} w-20`}
                  />
                  <button
                    data-testid={`edit-order-item-remove-${i}`}
                    onClick={() => removeItem(i)}
                    aria-label="Retirer"
                    className="shrink-0 rounded-full p-1.5 text-[#102C1F]/40 transition-colors duration-300 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
            <button
              data-testid="edit-order-add-item"
              onClick={addItem}
              className="mt-2 flex items-center gap-1.5 rounded-full border border-dashed border-[#102C1F]/40 px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#102C1F]/70 transition-colors duration-300 hover:border-[#102C1F] hover:text-[#102C1F]"
            >
              <Plus size={13} /> Ajouter un article
            </button>
          </div>

          <label className="block">
            <span className={labelCls}>Notes</span>
            <textarea
              data-testid="edit-order-notes"
              rows="2"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={field}
            />
          </label>

          <div className="grid grid-cols-2 items-end gap-3">
            <label className="block">
              <span className={labelCls}>Statut</span>
              <select
                data-testid="edit-order-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={field}
              >
                <option value="new">Nouvelle</option>
                <option value="preparation">En préparation</option>
                <option value="done">Remise</option>
              </select>
            </label>
            <p
              data-testid="edit-order-total"
              className="pb-2 text-right font-display text-xl italic"
            >
              Total : €{total.toFixed(2)}
            </p>
          </div>

          <button
            data-testid="edit-order-save"
            onClick={save}
            disabled={saving}
            className="w-full rounded-full bg-[#102C1F] px-5 py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030] disabled:opacity-50"
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
